import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { CLOSE_EVENT } from "./store_order_modal_controller"
import { syncGateReason } from "../lib/gate_reason"
import { buildGlAlert } from "../lib/gl_alert"

// Purchase Phone Number (#18494 / #17449) — the ONE place an agent buys a
// number. Mounted on the GOAT Store dialog root (or the deep-link page's
// wrapper) and posts to Agent::Crm::PhoneNumbersController#create, where every
// gate lives: the purchase lock, the CRM wallet charge, the free allowance and
// the Assistant refusal (Crm::PhoneNumbers::Provision). This is UX only.
//
// It replaced two separate buy flows (the Billing "CRM Sending Number" card
// and the Trust Center's inline box), so there is no twin to keep in step.
// Who listens for a purchase: My Numbers refetches, the Trust Center reveals
// its submit step, the GOAT Connect softphone takes the new caller ID.
export const PURCHASED_EVENT = "phone-numbers:purchased"
export const OUTBOUND_CALLER_ID_CHANGED_EVENT = "goat:softphone-outbound-caller-id-changed"
export const AREA_CODE_PATTERN = /^\d{3}$/
export const AREA_CODE_REQUIRED = "Enter a 3-digit area code."
// #18753: every number is named; the server refuses a blank name with the same sentence.
export const NUMBER_NAME_REQUIRED = "Give this number a name."
// Ready Gate (DESIGN.md PATTERN-3): what Buy Number still needs, by field label —
// the same labels Admin::PhoneNumberPurchaseForm paints on the first frame.
export const NUMBER_NAME_LABEL = "Number Name"
export const AREA_CODE_LABEL = "Area Code"
export const GENERIC_FAILURE = "We couldn't get your number. Please try again."
export const NETWORK_FAILURE = "Something went wrong. Please try again."
// Refusals the agent fixes by funding the CRM wallet (Provision#failure_status_for).
export const FUNDING_STATUSES = ["insufficient_funds", "wallet_missing"]
const TOLL_FREE = "toll_free"

// Stable token for one purchase attempt so a retried click is deduped
// server-side (Provision replays the same attempt instead of buying again).
// crypto.randomUUID is undefined on plain HTTP; fall back there.
export function buildAttemptId() {
  return globalThis.crypto?.randomUUID?.() || `phone-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

// +12085550161 → (208) 555-0161; anything else unchanged.
export function formatPhoneNumber(e164) {
  const digits = String(e164 || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "")
  return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : String(e164 || "")
}

// "GOAT Managed number (208) 555-0161 is ready to use." — the kind, worded by
// the server (Crm::PhoneNumbers::NumberCopy), says which account it landed on.
export function purchasedText(phoneNumber, ownershipLabel) {
  if (!phoneNumber) return "Your new number is ready to use."
  const number = formatPhoneNumber(phoneNumber)
  return ownershipLabel ? `${ownershipLabel} number ${number} is ready to use.` : `${number} is ready to use.`
}

export default class extends Controller {
  static targets = ["numberType", "typeCard", "numberKind", "kindCard", "kindField", "localField", "tollFreeField", "numberName", "areaCode", "tollFreePrefix", "buyButton", "message", "hint"]

  static values = { url: String, addFundsUrl: String }

  connect() {
    this.attemptId = buildAttemptId()
    this.busy = false
    this.typeChanged()
  }

  get numberType() {
    return this.numberTypeTargets.find((input) => input.checked)?.value || "local"
  }

  // #17600 Phase 4: GOAT Managed or Owned. The server paints the default (the
  // agent's Calling From switch) and greys Owned out before approval; the
  // server re-checks the pick on purchase. null when the dialog has no picker.
  // A toll-free number is always on the agent's own account, so it sends no kind.
  get numberKind() {
    if (this.numberType === TOLL_FREE) return null
    return this.numberKindTargets.find((input) => input.checked && !input.disabled)?.value || null
  }

  kindChanged() {
    this.kindCardTargets.forEach((card) => {
      card.classList.toggle("is-selected", Boolean(card.querySelector("input")?.checked))
    })
    this.clearMessage()
  }

  typeChanged() {
    const tollFree = this.numberType === TOLL_FREE
    if (this.hasLocalFieldTarget) this.localFieldTarget.hidden = tollFree
    if (this.hasTollFreeFieldTarget) this.tollFreeFieldTarget.hidden = !tollFree
    if (this.hasKindFieldTarget) this.kindFieldTarget.hidden = tollFree
    this.typeCardTargets.forEach((card) => {
      card.classList.toggle("is-selected", Boolean(card.querySelector("input")?.checked))
    })
    this.clearMessage()
    this.sync()
  }

  // Ready Gate (PATTERN-3): Buy Number stays disabled until the number is named
  // and, for a local number, the area code is 3 digits — the checks buy() and
  // the server make — and the hint line names what's left ("2 left: Number
  // Name · Area Code"). A button the server painted blocked (the Buy switch, no
  // carrier) has no buyButton target, so there is nothing to gate.
  sync() {
    if (!this.hasBuyButtonTarget) return
    const left = this.missing()
    const reason = left.length ? `${left.length} left: ${left.join(" · ")}` : ""
    if (this.hasHintTarget) this.hintTarget.textContent = reason
    syncGateReason(this.buyButtonTarget, { ready: left.length === 0, reason })
  }

  missing() {
    const left = []
    if (!(this.hasNumberNameTarget && this.numberNameTarget.value.trim())) left.push(NUMBER_NAME_LABEL)
    const areaCode = this.hasAreaCodeTarget ? this.areaCodeTarget.value.trim() : ""
    if (this.numberType !== TOLL_FREE && !AREA_CODE_PATTERN.test(areaCode)) left.push(AREA_CODE_LABEL)
    return left
  }

  // A sold-out area code answers with nearby codes; picking one fills the field.
  pickAlternative(event) {
    event.preventDefault()
    if (!this.hasAreaCodeTarget) return
    // Stimulus casts a numeric param to a Number; an area code is a string.
    this.areaCodeTarget.value = String(event.params.areaCode)
    this.areaCodeTarget.focus()
    this.clearMessage()
    this.sync()
  }

  async buy(event) {
    event.preventDefault()
    if (this.busy) return

    const numberName = this.hasNumberNameTarget ? this.numberNameTarget.value.trim() : ""
    if (!numberName) {
      this.showMessage({ text: NUMBER_NAME_REQUIRED, tone: "danger" })
      this.numberNameTarget?.focus()
      return
    }

    const tollFree = this.numberType === TOLL_FREE
    const areaCode = tollFree
      ? (this.hasTollFreePrefixTarget ? this.tollFreePrefixTarget.value : "")
      : (this.hasAreaCodeTarget ? this.areaCodeTarget.value.trim() : "")
    if (!tollFree && !AREA_CODE_PATTERN.test(areaCode)) {
      this.showMessage({ text: AREA_CODE_REQUIRED, tone: "danger" })
      this.areaCodeTarget?.focus()
      return
    }

    this.setBusy(true)
    try {
      const payload = {
        area_code: areaCode, number_type: this.numberType, friendly_name: numberName, client_attempt_id: this.attemptId,
      }
      if (this.numberKind) payload.number_kind = this.numberKind
      const response = await post(this.urlValue, {
        body: JSON.stringify(payload),
        contentType: "application/json",
        responseKind: "json",
      })
      const data = await this.readJson(response)
      if (response.ok) {
        this.succeeded(data)
      } else {
        this.refused(data)
      }
    } catch {
      // The attempt id is kept: a retry after a dropped connection replays the
      // same purchase server-side instead of buying a second number.
      this.showMessage({ text: NETWORK_FAILURE, tone: "danger" })
    } finally {
      this.setBusy(false)
    }
  }

  async readJson(response) {
    try {
      return (await response.json) || {}
    } catch {
      return {}
    }
  }

  succeeded(data) {
    // A fresh attempt for the next purchase; this one is done.
    this.attemptId = buildAttemptId()
    const phoneNumber = data?.purchased_number?.phone_number || null
    const outboundCallerId = data?.phone_number_setup?.outbound_caller_id
    const text = purchasedText(phoneNumber, data?.purchased_number?.ownership?.label)

    document.dispatchEvent(new CustomEvent(PURCHASED_EVENT, { detail: { phoneNumber } }))
    if (outboundCallerId !== undefined) {
      document.dispatchEvent(new CustomEvent(OUTBOUND_CALLER_ID_CHANGED_EVENT, { detail: { outboundCallerId } }))
    }

    if (this.inDialog) {
      window.showToast?.(text, "bg-success")
      document.dispatchEvent(new CustomEvent(CLOSE_EVENT))
    } else {
      this.showMessage({ text, tone: "success" })
    }
  }

  refused(data) {
    const errors = Array.isArray(data?.errors) ? data.errors.filter(Boolean) : []
    // An Assistant refusal (TeamActingPurchaseGuard) carries its sentence in `error`.
    const text = errors.length > 0 ? errors.join(" ") : (data?.code && data?.error) || GENERIC_FAILURE
    this.showMessage({
      text,
      tone: "danger",
      alternatives: Array.isArray(data?.alternative_area_codes) ? data.alternative_area_codes : [],
      addFunds: FUNDING_STATUSES.includes(data?.error),
    })
  }

  get inDialog() {
    return this.element.classList.contains("modal")
  }

  // BUTTON-8 / INTERACTION-6: a busy button keeps its label, is aria-busy +
  // disabled, and shows the spinner; when it settles the Ready Gate re-decides.
  setBusy(busy) {
    this.busy = busy
    if (!this.hasBuyButtonTarget) return
    const button = this.buyButtonTarget
    button.disabled = busy
    if (busy) {
      const spinner = document.createElement("span")
      spinner.className = "gl-btn-spinner"
      spinner.setAttribute("aria-hidden", "true")
      button.setAttribute("aria-busy", "true")
      button.prepend(spinner)
    } else {
      button.removeAttribute("aria-busy")
      button.querySelector(".gl-btn-spinner")?.remove()
      // Settled: the Ready Gate decides again (never re-enabled while a field is missing).
      this.sync()
    }
  }

  clearMessage() {
    if (!this.hasMessageTarget) return
    this.messageTarget.replaceChildren()
    this.messageTarget.hidden = true
  }

  // FORM-2: one gl-alert in the order card's live region, built from text
  // nodes only (the refusal sentence comes from the server).
  showMessage({ text, tone, alternatives = [], addFunds = false }) {
    if (!this.hasMessageTarget) return
    const sentence = document.createElement("p")
    sentence.className = "mb-0"
    sentence.textContent = text
    const body = [sentence]
    if (alternatives.length > 0) body.push(this.alternativeButtons(alternatives))
    if (addFunds) body.push(this.addFundsLink())
    const alert = buildGlAlert({
      tone,
      icon: tone === "success" ? "ph-check-circle" : "ph-warning-circle",
      className: "gl-alert--compact mb-0",
      role: null,
      body,
    })
    this.messageTarget.replaceChildren(alert)
    this.messageTarget.hidden = false
  }

  alternativeButtons(codes) {
    const row = document.createElement("div")
    row.className = "d-flex flex-wrap gap-2 mt-2"
    row.setAttribute("role", "group")
    row.setAttribute("aria-label", "Nearby area codes")
    codes.forEach((code) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "gl-btn gl-btn-outline gl-btn-sm"
      button.textContent = code
      button.setAttribute("aria-label", `Use area code ${code}`)
      button.dataset.action = "phone-number-purchase#pickAlternative"
      button.dataset.phoneNumberPurchaseAreaCodeParam = code
      row.append(button)
    })
    return row
  }

  addFundsLink() {
    const link = document.createElement("a")
    link.className = "gl-link d-inline-block mt-2"
    link.href = this.addFundsUrlValue
    link.textContent = "Add funds to your CRM wallet"
    return link
  }
}
